import axios from 'axios';

const API_URL = process.env.VUE_APP_API_URL || '';

/**
 * Document Type Registry
 * Contains metadata about different document types and their processing requirements
 */
const DOCUMENT_TYPES = {
  g702: {
    id: 'g702',
    name: 'G702 - Application for Payment',
    description: 'AIA G702 Application and Certificate for Payment',
    keyFields: [
      'project_name', 'application_number', 'period_to', 'contract_date',
      'architect', 'contractor', 'contract_sum', 'change_orders',
      'contract_sum_to_date', 'total_completed_stored', 'retainage',
      'total_earned_less_retainage', 'previous_certificates', 'current_payment',
      'balance_to_finish'
    ],
    identifiers: [
      'Application and Certificate for Payment',
      'AIA Document G702',
      'Contract Sum',
      'Total Completed and Stored to Date',
      'Retainage',
      'Current Payment Due'
    ]
  },
  g703: {
    id: 'g703',
    name: 'G703 - Continuation Sheet',
    description: 'AIA G703 Continuation Sheet for G702',
    keyFields: [
      'item_no', 'description', 'scheduled_value', 'work_completed_previous',
      'work_completed_current', 'materials_stored', 'total_completed_stored',
      'percentage', 'balance_to_finish', 'retainage'
    ],
    identifiers: [
      'Continuation Sheet',
      'AIA Document G703',
      'Item No.',
      'Scheduled Value',
      'Work Completed',
      'Materials Presently Stored'
    ]
  },
  budget: {
    id: 'budget',
    name: 'Developer Budget',
    description: 'Developer Budget Document',
    keyFields: [
      'line_item', 'description', 'budget_amount', 'actual_cost',
      'variance', 'percentage_complete'
    ],
    identifiers: [
      'Developer Budget',
      'Project Budget',
      'Budget Summary',
      'Cost Code'
    ]
  },
  gc_budget: {
    id: 'gc_budget',
    name: 'General Contractor Budget',
    description: 'General Contractor Budget Document',
    keyFields: [
      'line_item', 'description', 'budget_amount', 'actual_cost',
      'variance', 'percentage_complete'
    ],
    identifiers: [
      'General Contractor Budget',
      'GC Budget',
      'Construction Budget'
    ]
  },
  schedule: {
    id: 'schedule',
    name: 'Construction Schedule',
    description: 'Construction Schedule Document',
    keyFields: [
      'activity_id', 'description', 'duration', 'start_date',
      'finish_date', 'predecessors', 'successors'
    ],
    identifiers: [
      'Construction Schedule',
      'Project Schedule',
      'Activity ID',
      'Duration',
      'Start Date',
      'Finish Date'
    ]
  },
  invoice: {
    id: 'invoice',
    name: 'Soft Cost Invoice',
    description: 'Soft Cost Invoice Document',
    keyFields: [
      'invoice_number', 'invoice_date', 'vendor', 'description',
      'amount', 'due_date'
    ],
    identifiers: [
      'Invoice',
      'Bill To',
      'Invoice Number',
      'Invoice Date',
      'Amount Due'
    ]
  },
  requisition: {
    id: 'requisition',
    name: 'Borrower Requisition Letter',
    description: 'Borrower Requisition Letter Document',
    keyFields: [
      'requisition_number', 'date', 'project_name', 'borrower',
      'lender', 'amount_requested'
    ],
    identifiers: [
      'Requisition',
      'Requisition Letter',
      'Borrower',
      'Lender',
      'Amount Requested'
    ]
  },
  contract: {
    id: 'contract',
    name: 'Construction Contract',
    description: 'Construction Contract Document',
    keyFields: [
      'contract_number', 'date', 'owner', 'contractor',
      'project', 'contract_sum'
    ],
    identifiers: [
      'Construction Contract',
      'Agreement',
      'Contract Sum',
      'Scope of Work',
      'Terms and Conditions'
    ]
  },
  other: {
    id: 'other',
    name: 'Other Document',
    description: 'Other Document Type',
    keyFields: [],
    identifiers: []
  }
};

/**
 * Service for document classification operations
 */
export default {
  /**
   * Get all registered document types
   * @returns {Array} - Array of document types
   */
  getDocumentTypes() {
    return Object.values(DOCUMENT_TYPES);
  },

  /**
   * Get a specific document type by ID
   * @param {string} typeId - The ID of the document type
   * @returns {Object} - Document type object
   */
  getDocumentType(typeId) {
    return DOCUMENT_TYPES[typeId] || DOCUMENT_TYPES.other;
  },

  /**
   * Classify a document based on its content
   * @param {string} documentId - The ID of the document
   * @returns {Promise} - Promise with classification result
   */
  async classifyDocument(documentId) {
    try {
      const response = await axios.get(`${API_URL}/api/v1/documents/${documentId}/classify`);
      return response.data;
    } catch (error) {
      console.error('Error classifying document:', error);
      throw error;
    }
  },

  /**
   * Save document classification
   * @param {string} documentId - The ID of the document
   * @param {Object} classification - Classification data
   * @returns {Promise} - Promise with save result
   */
  async saveClassification(documentId, classification) {
    try {
      const response = await axios.post(`${API_URL}/api/v1/documents/${documentId}/classification`, {
        classification
      });
      return response.data;
    } catch (error) {
      console.error('Error saving document classification:', error);
      throw error;
    }
  },

  /**
   * Get document classification
   * @param {string} documentId - The ID of the document
   * @returns {Promise} - Promise with classification data
   */
  async getClassification(documentId) {
    try {
      const response = await axios.get(`${API_URL}/api/v1/documents/${documentId}/classification`);
      return response.data;
    } catch (error) {
      console.error('Error fetching document classification:', error);
      throw error;
    }
  }
};